/* 226 — the data layer for the agency (Harry). ONE seam, deliberately.
 *
 * 🔴 WHY THIS DUAL-EMITS. Every pixel on this store runs inside Shopify's Web
 * Pixels sandbox, whose iframe carries sandbox="allow-scripts allow-forms" with
 * NO allow-same-origin — so reading window.parent.dataLayer from a pixel throws
 * SecurityError. A theme-side window.dataLayer is therefore INVISIBLE to the
 * Google tag pixel and to any custom pixel as the store is configured today.
 * The supported bridge is window.Shopify.analytics.publish, so every event goes
 * out BOTH ways: pushed to window.dataLayer (for a container that ends up in the
 * main document) and published to the pixel sandbox. When Chris and Harry settle
 * where the GTM container lives, that decision changes THIS function and nothing
 * else — not five call sites.
 *
 * §11 stands: no vendor script is loaded here. This file emits events only.
 * NO PII EVER: form ids, an enquiry type, a product handle. Never a name,
 * email, phone or message body.
 */
(function () {
  window.dataLayer = window.dataLayer || [];
  const CTX = () => (window.__dr || {});

  function drTrack(event, payload) {
    const data = Object.assign({ event: event, surface: CTX().surface || null }, payload || {});
    // strip anything empty so the container never sees null keys it must guard
    Object.keys(data).forEach((k) => { if (data[k] === null || data[k] === undefined || data[k] === '') delete data[k]; });
    try { window.dataLayer.push(data); } catch (e) { /* never break the page for analytics */ }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(event, data);
      }
    } catch (e) { /* the sandbox may not be up yet; the dataLayer push still stands */ }
    return data;
  }
  window.drTrack = drTrack;

  /* ---- form submits -------------------------------------------------------
     A native Shopify contact form POSTs to /contact and 302s BACK with
     ?contact_posted=true; the success branch is then rendered server-side on the
     next load. So the honest "success" signal is that next page load — which
     also satisfies "fire on SUCCESS only, never on click" for free.
     ⚠ By then the field set has been REPLACED by the success message, so the
     hidden enquiry_type is gone. The context is therefore stashed at submit time
     and read back on the success load. The stash is not the event; nothing is
     emitted until the redirect proves the post succeeded. */
  const KEY = 'dr_form_pending';

  document.addEventListener('submit', function (e) {
    const form = e.target && e.target.closest ? e.target.closest('form[data-form-id]') : null;
    if (!form || form.dataset.newsletterProvider === 'klaviyo') return;
    const typeField = form.querySelector('[data-enquiry-type-field]');
    try {
      sessionStorage.setItem(KEY, JSON.stringify({
        form_id: form.dataset.formId || null,
        form_name: form.dataset.formName || null,
        enquiry_type: typeField ? typeField.value : null,
        product_handle: CTX().handle || null,
        surface: CTX().surface || null,
        at: 1,
      }));
    } catch (err) { /* private mode: fall back to the DOM read below */ }
  }, true);

  function emitFormSuccess() {
    let params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    const posted = params.get('contact_posted') === 'true' || params.get('customer_posted') === 'true';
    if (!posted) return;

    let stash = null;
    try { const raw = sessionStorage.getItem(KEY); if (raw) stash = JSON.parse(raw); } catch (e) {}
    try { sessionStorage.removeItem(KEY); } catch (e) {}
    if (stash && ['newsletter-footer', 'newsletter-contact'].includes(stash.form_id)
      && document.querySelector('form[data-newsletter-provider="klaviyo"][data-form-id="' + stash.form_id + '"]')) return;

    if (!stash) {
      /* No stash (a new tab, private mode, a bookmarked success URL). Fall back
         to the DOM. posted_successfully? is PAGE-wide, so a page holding more
         than one contact form cannot be attributed by presence alone — the hash
         Shopify appends to the form action (/contact#FormId) is the tie-break,
         and where even that is absent the event is marked ambiguous rather than
         guessed at or silently dropped. */
      const forms = [].slice.call(document.querySelectorAll('form[data-form-id]:not([data-newsletter-provider="klaviyo"])'));
      if (!forms.length) return;
      let form = null;
      const hash = (window.location.hash || '').replace('#', '');
      if (hash) form = forms.filter(function (f) { return f.id === hash; })[0] || null;
      if (!form && forms.length === 1) form = forms[0];
      if (!form) {
        drTrack('dr_form_submit', { form_id: forms[0].dataset.formId, form_name: forms[0].dataset.formName, attribution: 'ambiguous' });
        return;
      }
      stash = { form_id: form.dataset.formId, form_name: form.dataset.formName };
    }
    drTrack('dr_form_submit', {
      form_id: stash.form_id, form_name: stash.form_name,
      enquiry_type: stash.enquiry_type, product_handle: stash.product_handle,
    });
  }

  /* Klaviyo client acceptance. No PII and no click/submit event.
     The earlier deferred tracking asset loads before the footer asset. */
  const acceptedNewsletters = new WeakSet();
  document.addEventListener('dr:newsletter:accepted', function (event) {
    const form = event.target;
    if (!form || !form.matches || !form.matches('form[data-newsletter-provider="klaviyo"]:is([data-form-id="newsletter-footer"], [data-form-id="newsletter-contact"])')
      || !event.detail || event.detail.status !== 202 || acceptedNewsletters.has(form)) return;
    acceptedNewsletters.add(form);
    drTrack('dr_form_submit', { form_id: form.dataset.formId, form_name: form.dataset.formName });
  });

  /* ---- the APP form's placements -------------------------------------------
     🔴 SIX SURFACES SHARE ONE APP FORM DEFINITION (162127), so "the app form" is
     not an identity a tag container can bind to. Each placement carries its own
     data-form-id / data-form-name on its wrapper, and this reads the NEAREST
     wrapper to the form that submitted — never a global lookup, or every
     placement would report as whichever one happened to be first in the DOM.

     The app posts by fetch/XHR and shows an inline success, so there is no
     ?contact_posted redirect to key off. Success is therefore the app's own send
     request returning HTTP success AND an explicit app success result. The app
     also returns HTTP 200 for validation/CAPTCHA failures, so transport status
     alone is not a conversion. The submit merely records
     WHICH placement is in flight; nothing is emitted until the response lands. */
  const SEND_RE = /^https:\/\/app\.powerfulform\.com\/api\/front\/form\/[1-9]\d*\/send$/;
  const pendingByEndpoint = new Map();
  const quarantinedEndpoints = new Set();

  function sendEndpoint(value) {
    const url = String(value || '');
    return SEND_RE.test(url) ? url : null;
  }

  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!form || !form.getAttribute || !form.closest) return;
    const endpoint = sendEndpoint(form.getAttribute('action'));
    if (!endpoint || quarantinedEndpoints.has(endpoint)) return;
    // The app's form can have its own data-form-id. Prefer our placement,
    // which owns the stable identity and the live enquiry context.
    const wrap = form.closest('[data-app-placement]') || form.closest('[data-form-id]');
    if (!wrap) return;
    let type = wrap.dataset.enquiryType || null;
    // Retail layouts keep their contextual field outside the vendor form.
    // Read it at submission time: the same sheet also handles pre-orders.
    const typeField = wrap.querySelector('[data-enquiry-type-field]');
    if (typeField && typeField.value) type = typeField.value;
    const sel = [].slice.call(wrap.querySelectorAll('select'))
      .filter(function (x) { return [].slice.call(x.options).some(function (o) { return /trade-appointment|residential-project/.test(o.value); }); })[0];
    if (sel) type = sel.value || null;
    const prior = pendingByEndpoint.get(endpoint);
    // Different DOM forms sharing one app endpoint cannot be distinguished
    // at dispatch. A delayed CAPTCHA attempt may still send after another
    // attempt has failed, so quarantine this endpoint for this page lifetime.
    // This suppresses only uncertain analytics, never the form submissions.
    if (prior && prior.form.isConnected !== false && prior.form !== form) {
      quarantinedEndpoints.add(endpoint);
      pendingByEndpoint.delete(endpoint);
      return;
    }
    pendingByEndpoint.set(endpoint, {
      form: form,
      tracked: false,
      payload: Object.freeze({
        form_id: wrap.dataset.formId || null,
        form_name: wrap.dataset.formName || null,
        enquiry_type: type,
        product_handle: CTX().handle || null,
        surface: CTX().surface || null
      })
    });
  }, true);

  // The vendor emits beforeSubmit after validation, even for invalid forms.
  // An invalid attempt must not linger and contaminate a later placement.
  document.addEventListener('globo.formbuilder.form.beforeSubmit', function (event) {
    const form = event.detail && event.detail.form;
    if (!form || !form.getAttribute || !form.querySelector) return;
    const endpoint = sendEndpoint(form.getAttribute('action'));
    const pending = endpoint && pendingByEndpoint.get(endpoint);
    if (pending && pending.form === form && form.querySelector('.block-container.errors')) pendingByEndpoint.delete(endpoint);
  });

  function takePending(url, method) {
    const endpoint = sendEndpoint(url);
    if (!endpoint || quarantinedEndpoints.has(endpoint) || String(method || 'GET').toUpperCase() !== 'POST') return null;
    const pending = pendingByEndpoint.get(endpoint);
    pendingByEndpoint.delete(endpoint);
    if (!pending) return null;
    return pending;
  }

  function appSuccess(pending, okish, result) {
    if (!pending || pending.tracked || !okish || !result
      || (result.success !== true && result.success !== 'true')) return;
    pending.tracked = true;
    drTrack('dr_form_submit', pending.payload);
  }

  const _fetch = window.fetch;
  if (typeof _fetch === 'function') {
    window.fetch = function (input, init) {
      const url = (input && input.url) ? input.url : input;
      const method = (init && init.method) || (input && input.method) || 'GET';
      // Bind this immutable placement to this request before it leaves. A
      // later submit or out-of-order response cannot replace its identity.
      const pending = takePending(url, method);
      return _fetch.apply(this, arguments).then(function (res) {
        if (!pending || !res || !res.ok) return res;
        try {
          return res.clone().json().then(function (result) {
            try { appSuccess(pending, true, result); } catch (e) {}
            return res;
          }, function () { return res; });
        } catch (e) { /* unreadable or non-JSON response is not proven success */ }
        return res;
      });
    };
  }
  const _open = XMLHttpRequest.prototype.open;
  const _send = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (m, url) {
    const result = _open.apply(this, arguments);
    if (this.__drAppTrackingCleanup) this.__drAppTrackingCleanup();
    this.__drUrl = url;
    this.__drMethod = m;
    return result;
  };
  XMLHttpRequest.prototype.send = function () {
    const xhr = this;
    const pending = takePending(xhr.__drUrl, xhr.__drMethod);
    if (!pending) return _send.apply(this, arguments);
    if (xhr.__drAppTrackingCleanup) xhr.__drAppTrackingCleanup();
    let active = true;
    function cleanup() {
      active = false;
      if (typeof xhr.removeEventListener === 'function') {
        xhr.removeEventListener('load', loaded);
        ['error', 'abort', 'timeout', 'loadend'].forEach(function (name) { xhr.removeEventListener(name, cleanup); });
      }
      if (xhr.__drAppTrackingCleanup === cleanup) xhr.__drAppTrackingCleanup = null;
    }
    function loaded() {
      if (!active) return;
      cleanup();
      if (xhr.status < 200 || xhr.status >= 300) return;
      try {
        const result = xhr.responseType === 'json' ? xhr.response : JSON.parse(xhr.responseText);
        appSuccess(pending, true, result);
      } catch (e) { /* unreadable or non-JSON response is not proven success */ }
    }
    xhr.__drAppTrackingCleanup = cleanup;
    xhr.addEventListener('load', loaded);
    ['error', 'abort', 'timeout', 'loadend'].forEach(function (name) { xhr.addEventListener(name, cleanup); });
    try { return _send.apply(this, arguments); }
    catch (error) { cleanup(); throw error; }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', emitFormSuccess);
  else emitFormSuccess();
})();
